/**
 * Shrink an image in the browser before it is uploaded to S3.
 *
 * WHY: the admin form PUTs the raw File straight to a presigned S3 URL, with
 * nothing in between. Whatever comes off the camera is what gets stored and
 * what every visitor then downloads. Measured across a sample of the library:
 * the median photo is a reasonable 56 KB, but 18% are over 1 MB and 9% over
 * 3 MB, the largest being 7.9 MB. Those are uncompressed originals.
 *
 * They cost twice. Visitors download them on listing pages, which undoes much
 * of the transfer saving from enabling gzip; and WhatsApp will not build a
 * link preview from an image that size, so sharing a listing silently loses
 * its photograph.
 *
 * 1920px on the longest edge at 82% quality is comfortably more than the
 * largest size the site ever displays, and turns a 7.9 MB photo into roughly
 * 300 KB with no visible difference.
 *
 * GUARANTEE: this never breaks an upload. Anything unexpected - an
 * unsupported format, a decode failure, a browser without canvas - returns
 * the original file untouched, and it also refuses to return a file larger
 * than the one it was given.
 */

const MAX_EDGE = 1920;
const QUALITY = 0.82;

// Below this, re-encoding gains nothing and risks losing quality for free.
const SKIP_BELOW_BYTES = 400 * 1024;

/**
 * Decode to something drawable, preserving EXIF orientation.
 *
 * createImageBitmap with imageOrientation honours the camera's rotation flag;
 * drawImage on a plain <img> historically did not, which is how photos end up
 * sideways after a resize. The <img> path is the fallback for browsers
 * without it, and modern ones apply orientation there too.
 */
function decode(file) {
    if (typeof createImageBitmap === "function") {
        try {
            return createImageBitmap(file, { imageOrientation: "from-image" });
        } catch (e) {
            // Older implementations reject the options argument; fall through.
        }
    }

    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (err) => {
            URL.revokeObjectURL(url);
            reject(err);
        };
        img.src = url;
    });
}

export default async function compressImage(file) {
    if (!file || !file.type || file.type.indexOf("image/") !== 0) return file;

    // Canvas would flatten an animated GIF to a single frame.
    if (file.type === "image/gif") return file;
    if (file.size <= SKIP_BELOW_BYTES) return file;

    let source;
    try {
        source = await decode(file);
    } catch (e) {
        return file;
    }

    const width = source.width || source.naturalWidth;
    const height = source.height || source.naturalHeight;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    let blob;
    try {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d");
        if (!ctx) return file;
        ctx.drawImage(source, 0, 0, w, h);

        blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
    } catch (e) {
        return file;
    } finally {
        if (source.close) source.close(); // release an ImageBitmap
    }

    // Re-encoding an already-optimised JPEG can grow it. Never upload more
    // bytes than we were handed.
    if (!blob || blob.size >= file.size) return file;

    // S3 keys are always written with a .jpg extension (api/routes/s3url.js),
    // so a JPEG blob matches what the object claims to be.
    return new File([blob], file.name, { type: "image/jpeg", lastModified: Date.now() });
}
